/* ════════════ THE RELAY ════════════
   The five-eyes page's atmosphere: five dim hubs, packets hopping
   between random pairs, flaring amber at each handoff. Collected
   over there, delivered over here. Nobody broke their own law. */

import { onPage } from "./router";

const REDUCED = matchMedia("(prefers-reduced-motion: reduce)").matches;

function relay(signal: AbortSignal) {
  const c = document.querySelector<HTMLCanvasElement>("#relay")!;
  const ctx = c.getContext("2d")!;

  const AMBER = "#ff9e2e";
  const GREY = "#9aa0a6";

  type Hub = { x: number; y: number };
  type Packet = {
    from: number;
    to: number;
    p: number; // progress 0..1 along the line
    speed: number;
    col: string;
    flare: number; // frames of handoff flash left
  };
  let hubs: Hub[] = [];
  let packets: Packet[] = [];

  const spawn = (at = -1): Packet => {
    const isHot = Math.random() < 0.18;
    const from = at >= 0 ? at : (Math.random() * hubs.length) | 0;
    let to = (Math.random() * hubs.length) | 0;
    if (to === from) to = (to + 1) % hubs.length;
    return {
      from,
      to,
      p: 0,
      speed: 0.0022 + Math.random() * 0.0042,
      col: isHot ? AMBER : GREY,
      flare: 0,
    };
  };

  const seed = () => {
    c.width = innerWidth;
    c.height = innerHeight;
    // five hubs, loose pentagon stretched across the viewport
    hubs = [
      { x: 0.5, y: 0.16 },
      { x: 0.88, y: 0.4 },
      { x: 0.73, y: 0.84 },
      { x: 0.27, y: 0.84 },
      { x: 0.12, y: 0.4 },
    ].map((h) => ({ x: h.x * c.width, y: h.y * c.height }));
    const count = Math.min(20, Math.floor((c.width * c.height) / 80000));
    packets = Array.from({ length: count }, () => {
      const k = spawn();
      k.p = Math.random(); // desync
      return k;
    });
  };
  seed();
  addEventListener("resize", seed, { signal });

  const draw = () => {
    ctx.clearRect(0, 0, c.width, c.height);
    // the standing lines: every hub pair, barely there
    ctx.globalAlpha = 0.05;
    ctx.strokeStyle = GREY;
    ctx.lineWidth = 1;
    for (let i = 0; i < hubs.length; i++) {
      for (let j = i + 1; j < hubs.length; j++) {
        ctx.beginPath();
        ctx.moveTo(hubs[i]!.x, hubs[i]!.y);
        ctx.lineTo(hubs[j]!.x, hubs[j]!.y);
        ctx.stroke();
      }
    }
    // the hubs: dim amber squares, always on, never named
    ctx.globalAlpha = 0.22;
    ctx.fillStyle = AMBER;
    for (const h of hubs) ctx.fillRect(h.x - 2, h.y - 2, 4, 4);
    // the packets
    for (const k of packets) {
      const a = hubs[k.from]!;
      const b = hubs[k.to]!;
      const x = a.x + (b.x - a.x) * k.p;
      const y = a.y + (b.y - a.y) * k.p;
      const hot = k.col === AMBER;
      ctx.globalAlpha = hot ? 0.45 : 0.18;
      ctx.fillStyle = k.col;
      ctx.fillRect(x - 1.5, y - 1.5, 3, 3);
      // handoff flare: a brief amber ring at the receiving hub
      if (k.flare > 0) {
        const f = k.flare / 14;
        ctx.globalAlpha = f * 0.45;
        ctx.strokeStyle = AMBER;
        ctx.beginPath();
        ctx.arc(a.x, a.y, 3 + (1 - f) * 9, 0, Math.PI * 2);
        ctx.stroke();
      }
    }
    ctx.globalAlpha = 1;
  };

  if (REDUCED) {
    draw();
    return;
  }
  const tick = () => {
    if (signal.aborted) return;
    for (let i = 0; i < packets.length; i++) {
      const k = packets[i]!;
      if (k.flare > 0) k.flare--;
      k.p += k.speed;
      if (k.p >= 1) {
        // arrival: respawn from the receiving hub, flag the handoff
        const next = spawn(k.to);
        next.flare = 14;
        packets[i] = next;
      }
    }
    draw();
    requestAnimationFrame(tick);
  };
  tick();
}

onPage(relay);
